import Button from '@atlaskit/button';
import InboxIcon from '@atlaskit/icon/core/inbox';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import { token } from '@atlaskit/tokens';
import DropdownMenu, {
  DropdownItem,
  DropdownItemGroup,
  type DropdownMenuProps,
  type OnOpenChangeArgs,
} from '@databases/atlaskit/DropdownMenu';
import { openPrintEntryPopup } from '@databases/export';
import { getSelectDatabaseDraft } from '@databases/fields/utils';
import { messages } from '@databases/intl/messages';
import usePanel from '@databases/panel/usePanel';
import { defaultStoreUtils } from '@databases/redux';
import React, { type FunctionComponent, useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl-next';
import styled from 'styled-components';

import { getIdentity } from '@canvas/connection/identity';

import SlideTransition from '../SlideTransition';

export const ImmediateEntryActionsContainer = styled.div`
  &,
  > div {
    display: flex;
    align-items: center;
  }

  > * + * {
    margin-left: ${token('space.050')};
  }
`;

export type ImmediateEntryActionKey = 'print' | 'openDatabase' | 'discard' | 'delete';

const defaultTrigger: DropdownMenuProps['trigger'] = ({ triggerRef, ...props }) => (
  <Button iconBefore={<ShowMoreHorizontalIcon label="" />} ref={triggerRef} {...props} />
);

export interface ImmediateEntryActionsProps {
  contentId: string;
  entryId: string;
  placement?: DropdownMenuProps['placement'];
  show?: ImmediateEntryActionKey[];
}

/**
 * Buttons to perform various actions on database entries. Actions are applied immediately when clicking them, rather than scheduling them for later.
 */
const ImmediateEntryActions: FunctionComponent<ImmediateEntryActionsProps> = ({
  contentId,
  entryId,
  placement,
  show,
}) => {
  const store = defaultStoreUtils.useStore();

  const [showDevMenu, setShowDevMenu] = useState(false);
  const { showPanel } = usePanel();

  useEffect(() => {
    const loadDevMenu = async () => {
      const identity = await getIdentity();
      if (identity.email?.endsWith('@atlassian.com')) {
        setShowDevMenu(true);
      }
    };

    loadDevMenu();
  }, []);

  const intl = useIntl();
  const { formatMessage } = intl;

  const handlePrint = useCallback(async () => {
    await openPrintEntryPopup(
      {
        contentId,
        entryId,
        selectDatabase: getSelectDatabaseDraft(true),
      },
      store.getState()
    );
  }, [contentId, entryId, store]);

  const [isOpen, setIsOpen] = useState(false);
  const handleOpenChange = useCallback((attrs: OnOpenChangeArgs) => {
    setIsOpen(attrs.isOpen);
  }, []);

  const actions = {
    print: !show || show.includes('print'),
  };
  const hasAnyActions = Object.values(actions).filter((a) => a).length > 0;

  return (
    <ImmediateEntryActionsContainer className={isOpen ? 'isOpen' : ''}>
      <SlideTransition isVisible={hasAnyActions}>
        <DropdownMenu
          trigger={defaultTrigger}
          isOpen={isOpen}
          onOpenChange={handleOpenChange}
          placement={placement}
        >
          <DropdownItemGroup>
            {actions.print && (
              <DropdownItem
                elemBefore={<InboxIcon spacing="spacious" label="" />}
                onClick={handlePrint}
              >
                {formatMessage(messages['entry-actions.print-button-text'])}
              </DropdownItem>
            )}
          </DropdownItemGroup>
          {showDevMenu && (
            <DropdownItemGroup>
              <DropdownItem
                onClick={() => {
                  showPanel('DevtoolsPanel');
                }}
              >
                {formatMessage(messages['actions-menu.devtools'])}
              </DropdownItem>
            </DropdownItemGroup>
          )}
        </DropdownMenu>
      </SlideTransition>
    </ImmediateEntryActionsContainer>
  );
};

export default ImmediateEntryActions;
